<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import FormInput from 'v3/components/Form/Input.vue';
import FormSelect from 'v3/components/Form/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: { type: String, default: 'create' }, // 'create' | 'edit'
  board: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();

const teams = computed(() => store.getters['teams/getTeams'] || []);
const ui = computed(() => store.getters['boards/getUIFlags']);

const truncateTeamName = name => {
  return name.length > 25 ? `${name.slice(0, 25)}...` : name;
};

const state = reactive({
  team_id: props.board?.team_id ? String(props.board.team_id) : '',
  name: props.board?.name || '',
  columns: (props.board?.board_columns || []).map(c => ({
    id: c.id,
    name: c.name,
  })) || [{ name: '' }, { name: '' }, { name: '' }],
});

// Keep a snapshot of original columns to restore on error
const originalColumnsSnapshot = ref(null);

const nameError = reactive({
  hasError: false,
  message: '',
});

const teamIdError = reactive({
  hasError: false,
  message: '',
});

watch(
  [() => props.board, () => props.mode],
  ([newBoard, mode]) => {
    if (mode === 'edit' && newBoard) {
      state.team_id = newBoard.team_id ? String(newBoard.team_id) : '';
      state.name = newBoard.name || '';
      const columns = (newBoard.board_columns || []).map(c => ({
        id: c.id,
        name: c.name,
      })) || [{ name: '' }, { name: '' }, { name: '' }];
      state.columns = columns;
      // Save snapshot of original columns for error recovery
      originalColumnsSnapshot.value = JSON.parse(JSON.stringify(columns));
    } else if (mode === 'create') {
      state.team_id = '';
      state.name = '';
      state.columns = [{ name: '' }, { name: '' }, { name: '' }];
      originalColumnsSnapshot.value = null;
    }
    nameError.hasError = false;
    nameError.message = '';
    teamIdError.hasError = false;
    teamIdError.message = '';
  },
  { immediate: true }
);

watch(
  () => state.name,
  () => {
    if (nameError.hasError) {
      nameError.hasError = false;
      nameError.message = '';
    }
  }
);

watch(
  () => state.team_id,
  () => {
    if (teamIdError.hasError) {
      teamIdError.hasError = false;
      teamIdError.message = '';
    }
  }
);

const addColumn = () => state.columns.push({ name: '' });
const removeColumn = idx => {
  if (state.columns.length > 1) {
    state.columns.splice(idx, 1);
  }
};

const translateError = (errorMessage, field) => {
  if (!errorMessage) return errorMessage;

  const errorLower = errorMessage.toLowerCase();

  // Name field errors
  if (field === 'name') {
    if (errorLower.includes('too long') || errorLower.includes('maximum')) {
      return t('KANBAN.ERRORS.NAME_TOO_LONG');
    }
    if (errorLower.includes("can't be blank") || errorLower.includes('blank')) {
      return t('KANBAN.ERRORS.NAME_REQUIRED');
    }
  }

  // Team ID field errors
  if (field === 'team_id') {
    if (errorLower.includes('taken') || errorLower.includes('already')) {
      return t('KANBAN.ERRORS.TEAM_ID_UNIQUE');
    }
    if (errorLower.includes("can't be blank") || errorLower.includes('blank')) {
      return t('KANBAN.ERRORS.TEAM_ID_REQUIRED');
    }
  }

  // Board columns errors
  if (field === 'board_columns') {
    if (
      errorLower.includes('at least one') ||
      errorLower.includes('must have')
    ) {
      return t('KANBAN.ERRORS.BOARD_COLUMNS_AT_LEAST_ONE');
    }
  }

  // Fallback to original message if no translation found
  return errorMessage;
};

const onCancel = () => emit('close');

const onSave = async () => {
  const validColumns = state.columns
    .filter(c => c.name && c.name.trim().length)
    .map((c, idx) => ({ id: c.id, name: c.name.trim(), position: idx }));

  if (validColumns.length === 0) {
    useAlert('É necessário ter pelo menos uma coluna no quadro.');
    return;
  }

  // Build board_columns_attributes with _destroy for deleted columns
  const originalColumnIds = (props.board?.board_columns || []).map(c => c.id).filter(Boolean);
  const currentColumnIds = validColumns.map(c => c.id).filter(Boolean);
  const deletedColumnIds = originalColumnIds.filter(id => !currentColumnIds.includes(id));
  
  const boardColumnsAttributes = [...validColumns];
  if (props.mode === 'edit' && props.board?.id) {
    deletedColumnIds.forEach(columnId => {
      boardColumnsAttributes.push({ id: columnId, _destroy: true });
    });
  }

  const payload = {
    name: state.name,
    team_id: state.team_id ? Number(state.team_id) : null,
    board_columns_attributes: boardColumnsAttributes,
  };

  try {
    if (props.mode === 'edit' && props.board?.id) {
      await store.dispatch('boards/update', { id: props.board.id, payload });
      if (deletedColumnIds.length > 0) {
        useAlert('Coluna(s) removida(s) com sucesso!', { duration: 3000 });
      }
    } else {
      await store.dispatch('boards/create', payload);
    }
    emit('saved');
    emit('close');
  } catch (error) {
    // Restore original columns on error to allow user to see and fix
    if (props.mode === 'edit' && originalColumnsSnapshot.value) {
      state.columns = JSON.parse(JSON.stringify(originalColumnsSnapshot.value));
    }
    
    if (error?.response?.data?.errors?.team_id) {
      const teamIdErrors = error.response.data.errors.team_id;
      teamIdError.hasError = true;
      const errorMsg = Array.isArray(teamIdErrors)
        ? teamIdErrors[0]
        : teamIdErrors;
      teamIdError.message = translateError(errorMsg, 'team_id');
    } else if (error?.response?.data?.errors?.name) {
      const nameErrors = error.response.data.errors.name;
      nameError.hasError = true;
      const errorMsg = Array.isArray(nameErrors) ? nameErrors[0] : nameErrors;
      nameError.message = translateError(errorMsg, 'name');
    } else if (error?.response?.data?.errors?.board_columns) {
      const boardColumnsErrors = error.response.data.errors.board_columns;
      const errorMsg = Array.isArray(boardColumnsErrors)
        ? boardColumnsErrors[0]
        : boardColumnsErrors;
      useAlert(translateError(errorMsg, 'board_columns'), { duration: 6000 });
    } else if (error?.response?.data?.message) {
      useAlert(error.response.data.message, { duration: 6000 });
    } else {
      useAlert(
        error?.message ||
          t('KANBAN.ERRORS.GENERAL') ||
          'Erro ao salvar o quadro. Tente novamente.',
        { duration: 6000 }
      );
    }
    // Do not close modal on error - user needs to see the restored columns
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="mode === 'edit' ? 'Editar quadro' : 'Novo quadro'"
      header-content="Defina o time, nome e colunas do quadro"
    />
    <div class="p-4 space-y-4">
      <FormSelect
        :key="`team-select-${props.board?.id || 'create'}`"
        v-model="state.team_id"
        name="team"
        spacing="compact"
        label="Time"
        :has-error="teamIdError.hasError"
        :error-message="teamIdError.message"
      >
        <option value="" disabled>{{ t('KANBAN.SELECT_TEAM') }}</option>
        <option
          v-for="team in teams"
          :key="team.id"
          :value="String(team.id)"
          :selected="String(team.id) === state.team_id"
        >
          {{ truncateTeamName(team.name) }}
        </option>
      </FormSelect>

      <FormInput
        v-model="state.name"
        name="boardName"
        spacing="compact"
        label="Nome do quadro"
        placeholder="Ex: Quadro 1"
        :has-error="nameError.hasError"
        :error-message="nameError.message"
      />

      <div class="border border-n-weak bg-n-solid-1 rounded-lg p-4">
        <div class="flex items-center justify-between mb-2">
          <label class="block text-sm font-medium">{{
            t('KANBAN.COLUMNS')
          }}</label>
          <Button
            size="sm"
            icon="i-lucide-plus"
            label="Adicionar coluna"
            @click="addColumn"
          />
        </div>
        <div class="space-y-3">
          <div
            v-for="(col, idx) in state.columns"
            :key="idx"
            class="flex gap-2 items-center"
          >
            <div class="-mb-4">
              <FormInput
                v-model="col.name"
                class="flex-1"
                :name="`column_${idx}`"
                spacing="compact"
                label=""
              />
            </div>
            <div>
              <Button
                xs
                ruby
                faded
                icon="i-lucide-trash-2"
                :disabled="state.columns.length === 1"
                @click="removeColumn(idx)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button faded slate label="Cancelar" @click="onCancel" />
        <Button
          :disabled="!state.team_id || !state.name.trim() || ui.isUpdating"
          :label="
            ui.isUpdating
              ? 'Salvando...'
              : mode === 'edit'
                ? 'Atualizar'
                : 'Criar'
          "
          icon="i-lucide-save"
          @click="onSave"
        />
      </div>
    </div>
  </div>
</template>
